<template>
  <div class="rounded-md border p-5 text-sm xl:p-6" data-no-tracking>
    <div class="grid grid-cols-[min-content_1fr] gap-4">
      <ProductImage
        :product-media="contractImage"
        class="w-20"
        fallback-class="product-image flex-shrink-0 object-contain object-center aspect-[74/100] rounded-lg bg-light-grey p-2"
        main-class="product-image flex-shrink-0 object-contain object-center aspect-[74/100] rounded-lg bg-light-grey"
      />

      <div class="flex flex-col gap-2">
        <SubscriptionStatus :status="contract?.status" class="self-start" />

        <div class="grid grid-cols-2">
          <div class="flex flex-col">
            <div class="text-xl font-medium">
              {{ contractName }}
              <CommonLabel
                v-if="isSubscription"
                path="BWF/shop/account/subscriptions/contract/subscriptionLabel"
              />
            </div>
            <Tint v-if="isSubscription" class="text-sm">
              <CommonLabel path="BWF/shop/account/orderDetails/orderNumber" />&nbsp;<span>{{
                subscriptionOrderNumber
              }}</span>
            </Tint>
          </div>

          <div class="hidden justify-self-end xl:block">
            <TextSelectField
              ref="subscriptionActionSelectField"
              v-model="selectedMenuOption"
              :label="menuActionLabel"
              :options="menuOptions"
            >
              <CommonLabel
                v-if="isSubscription"
                class="text-sm"
                path="BWF/shop/account/subscriptions/contract/subscriptionActions"
              />
              <CommonLabel
                v-else
                class="text-sm"
                path="BWF/shop/account/subscriptions/contract/exchangeboxActions"
              />
            </TextSelectField>
          </div>
        </div>

        <Divider class="my-4 xl:my-0" />

        <div class="grid gap-4 hyphens-auto xl:grid-cols-3">
          <div class="flex flex-col gap-2">
            <CommonLabel class="font-medium" path="BWF/shop/shared/shippingAddress" />
            <div>
              {{ `${contract?.shippingAddress?.firstName} ${contract?.shippingAddress?.lastName}`
              }}<br />
              {{ contract?.shippingAddress?.street }}<br />
              <template v-if="contract?.shippingAddress?.additionalAddressLine1"
                >{{ contract?.shippingAddress?.additionalAddressLine1 }}<br
              /></template>
              {{ contract?.shippingAddress?.zipcode }} {{ contract?.shippingAddress?.city }}<br />
              {{ contract?.shippingAddress?.country?.name }}
            </div>
          </div>

          <ContractSubscriptionInformation v-if="isSubscription" :contract="contract" />
          <ContractExchangeboxInformation v-else :contract="contract" :plan="plan" />

          <div class="flex flex-col items-start gap-2">
            <CommonLabel
              class="font-medium"
              path="BWF/shop/account/subscriptions/contract/paymentLabel"
            />
            <div>
              {{ contract?.paymentMethod?.name }}
            </div>
            <CommonLabel
              v-if="creditCardNumber"
              :placeholder="{ creditCardNumber }"
              path="BWF/shop/account/subscriptions/contract/creditCardNumber"
            />
            <Tint v-if="paymentExpired || paymentExpiringSoon" color="failure" tag="div">
              <CommonLabel
                v-if="paymentExpired"
                path="BWF/shop/account/subscriptions/contract/paymentExpired"
              />
              <CommonLabel
                v-if="paymentExpiringSoon"
                path="BWF/shop/account/subscriptions/contract/paymentExpiringSoon"
              />
              <button class="text-link" @click="$emit('contract-change-payment')">
                <CommonLabel path="BWF/shop/account/subscriptions/contract/updatePaymentMethod" />
              </button>
            </Tint>
            <span v-else>{{ contract?.customFields?.paymentDetails?.consumerAccount }}</span>
          </div>
        </div>
      </div>
      <div class="col-span-full mt-4 xl:hidden">
        <Divider />
        <TextSelectField
          ref="orderActionSelectField"
          v-model="selectedMenuOption"
          :label="menuActionLabel"
          :options="menuOptions"
          class="justify-center p-4 pb-0"
        >
          <CommonLabel
            v-if="isSubscription"
            class="text-sm"
            path="BWF/shop/account/subscriptions/contract/subscriptionActions"
          />
          <CommonLabel
            v-else
            class="text-sm"
            path="BWF/shop/account/subscriptions/contract/exchangeboxActions"
          />
        </TextSelectField>
      </div>
    </div>

    <ModalComponent :controller="addressModal">
      <template #headline>
        <p class="block text-center text-lg md:text-2xl">
          <CommonLabel
            path="BWF/shop/account/subscriptions/contract/subscriptionActionAddressEdit"
          />
        </p>
      </template>
      <AccountAddressForm
        :filter-shipping-countries="true"
        :address="contract?.shippingAddress"
        @close-modal="addressModal.close()"
        @address-submit="onAddressSave"
      />
    </ModalComponent>
  </div>
</template>

<script setup lang="ts">
import { Divider } from "~/components/clarity-ui/Divider";
import { Tint } from "~/components/clarity-ui/Tint";
import TextSelectField from "~/templates/elements/form/TextSelectField.vue";
import CommonLabel from "~/templates/components/CommonLabel.vue";
import AccountAddressForm from "~/templates/components/shop/account/AccountAddressForm.vue";
import ModalComponent from "~/templates/components/ModalComponent.vue";
import ProductImage from "~/templates/elements/ProductImage.vue";
import ContractSubscriptionInformation from "~/templates/elements/ContractSubscriptionInformation.vue";
import ContractExchangeboxInformation from "~/templates/elements/ContractExchangeboxInformation.vue";
import { isCancelled } from "~/utils/helper/shop/subscription";

export interface SubscriptionContractProps {
  contract: object;
  cancellationModalArea?: object;
  cancellationSuccessModalArea?: object;
}

const props = defineProps<SubscriptionContractProps>();

const { getLabels } = useCommonLabels();
const { apiInstance } = useShopwareContext();
const { pushSuccess, pushError } = useNotifications();

const emit = defineEmits<{
  (e: "contract-update"): void;
  (e: "contract-cancel"): void;
  (e: "subscription-contract-cancel"): void;
  (e: "contract-change-payment"): void;
  (e: "subscription-contract-pause"): void;
}>();

const commonLabels = getLabels("BWF/shop/account/subscriptions/contract", [
  "exchangeboxActions",
  "exchangeboxActionCancel",
  "subscriptionActions",
  "subscriptionActionCancel",
  "subscriptionChangePayment",
  "cancellationReasons",
  "subscriptionActionAddressEdit",
  "setAddressSuccess",
]);

const plan = computed(() => props?.contract?.contractPlan);

const contractName = computed(() =>
  isSubscription.value
    ? `${props.contract?.contractProducts[0]?.product?.translated?.name} (${props.contract?.contractLabel})`
    : plan.value?.name,
);

const contractImage = computed(
  () =>
    props?.contract?.contractPlan?.product?.cover?.media ??
    props?.contract?.contractProducts?.[0]?.product?.cover?.media,
);

// TODO cleanup / move to composable ?
const isSubscription = computed(() => props?.contract?.contractType == "subscription");
const subscriptionOrderNumber = computed(
  () => props?.contract?.webContractOrders?.[0]?.order.orderNumber,
);

//
const addressModal = useModal();

const menuActionLabel = computed(() => {
  if (isSubscription.value) {
    return commonLabels.subscriptionActions;
  } else {
    return commonLabels.exchangeboxActions;
  }
});

const isPauseAllowed = computed(() => {
  return props?.contract?.extensions?.contractPause?.isContractPauseAllowed;
});

const menuOptions = computed(() => {
  return [
    {
      label: commonLabels.subscriptionActionAddressEdit,
      value: "addressEdit",
      disabled: isCancelled(props?.contract),
    },
    {
      label: commonLabels.subscriptionChangePayment,
      value: "payment",
      disabled: isCancelled(props?.contract),
    },
    {
      label: isSubscription.value
        ? commonLabels.subscriptionActionCancel
        : commonLabels.exchangeboxActionCancel,
      value: isSubscription.value ? "subscription-cancel" : "cancel",
      disabled: isCancelled(props?.contract),
    },
    ...(isSubscription.value
      ? [
          {
            label: commonLabels.subscriptionActionPause,
            value: "subscription-pause",
            disabled: isCancelled(props?.contract) || !isPauseAllowed.value,
          },
        ]
      : []),
  ];
});

const creditCardNumber = computed(
  () => props?.contract?.customFields?.paymentDetails?.cardNumber ?? false,
);

const subscriptionActionSelectField = ref(null);
const selectedMenuOption = ref("");

watch(selectedMenuOption, (option) => {
  switch (option) {
    case "addressEdit":
      addressModal.open();
      break;
    case "cancel":
      emit("contract-cancel");
      break;
    case "subscription-cancel":
      emit("subscription-contract-cancel");
      break;
    case "payment":
      emit("contract-change-payment");
      break;
    case "subscription-pause":
      emit("subscription-contract-pause");
      break;
  }

  // reset options
  subscriptionActionSelectField.value?.reset();
});

const onAddressSave = async (formState) => {
  try {
    const { error } = await apiInstance.invoke.patch(
      `/store-api/contract/${props.contract?.id}/address/${props.contract?.shippingAddress?.id}`,
      formState,
    );
    if (error?.value) {
      pushError(error?.value?.messages?.[0]?.detail);
    } else {
      pushSuccess(commonLabels.setAddressSuccess);
    }
    addressModal.close();
    emit("contract-update");
  } catch (e) {
    pushError(e?.messages?.[0]?.detail);
  }
};

const paymentExpiringSoon = ref(false);
const paymentExpired = ref(false);

const checkExpiration = () => {
  const expiryState = props?.contract?.extensions?.creditCardExpiryState?.value;
  if (expiryState === "expired" || props?.contract?.status === "payment_update_requested") {
    paymentExpired.value = true;
  }
  if (expiryState === "expiringSoon") {
    paymentExpiringSoon.value = true;
  }
};

onMounted(() => {
  checkExpiration();
});
</script>
